import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { User } from '../types';
import {
  DISC_SERIES,
  DISC_ADAPTED_BAR_FILL,
  VALORES_COMPORTAMENTAIS,
  TRAIT_ROWS,
  BehavioralFormKey,
} from '../constants/userBehavioral';

interface BehavioralChartsPreviewProps {
  user: User | null;
  /** When false, the internal "Perfil comportamental" header is hidden
   *  (use when the parent already renders a section header). */
  showHeader?: boolean;
}

// ---- Flat/high-contrast chart palette (matches the app theme) ----
const AXIS_TICK = '#555555';
const GRID_STROKE = '#e0e0e0';
const REF_LINE = '#cccccc';
const BLUE = '#0057ff';
const RED = '#d32f2f';

const TOOLTIP_STYLE = {
  background: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: 0,
  color: '#000000',
  boxShadow: 'none',
} as const;

const refLineStyle = {
  stroke: REF_LINE,
  strokeWidth: 1,
  strokeDasharray: '4 4',
  strokeOpacity: 1,
} as const;

function getNumeric(u: User | null, key: BehavioralFormKey): number {
  if (!u) return 0;
  const v = u[key as keyof User];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

// ---- DISC spectrum (mapa de tendência em 2 eixos) ----
// Eixo X = Foco: tarefa (−) ↔ pessoas (+). Eixo Y = Ritmo: reservado (−) ↔ acelerado (+).
// D = acelerado + tarefa | I = acelerado + pessoas | S = reservado + pessoas | C = reservado + tarefa.
const DISC_META: Record<'D' | 'I' | 'S' | 'C', { label: string; fill: string }> = {
  D: { label: 'Dominância', fill: '#ef4444' },
  I: { label: 'Influência', fill: '#eab308' },
  S: { label: 'Estabilidade', fill: '#22c55e' },
  C: { label: 'Conformidade', fill: '#3b82f6' },
};

type DiscKey = 'D' | 'I' | 'S' | 'C';

interface DiscDimShare {
  key: DiscKey;
  label: string;
  fill: string;
  value: number;
  /** Percentual do total DISC (0–100). */
  share: number;
  /** Dimensão significativa (compõe o padrão/blend). */
  significant: boolean;
}

interface DiscPoint {
  x: number;
  y: number;
  /** Padrão DISC ordenado, ex.: "D", "DC", "DCS". */
  pattern: string;
  /** Rótulo legível, ex.: "Dominância + Conformidade". */
  patternLabel: string;
  /** Todas as dimensões, ordenadas do maior para o menor. */
  ranked: DiscDimShare[];
}

/**
 * Um perfil DISC quase nunca é uma letra só — é um blend (DC, DCS, ...).
 * O padrão inclui a dimensão mais forte e as seguintes que forem "elevadas":
 * dentro de 25% do topo E acima da média das quatro dimensões.
 */
function discSpectrumPoint(
  d: number,
  i: number,
  s: number,
  c: number,
): DiscPoint | null {
  const total = d + i + s + c;
  if (total <= 0) return null;
  const x = (i + s - (d + c)) / total; // pessoas(+) vs tarefa(−)
  const y = (d + i - (s + c)) / total; // acelerado(+) vs reservado(−)

  const raw: Array<{ key: DiscKey; value: number }> = [
    { key: 'D', value: d },
    { key: 'I', value: i },
    { key: 'S', value: s },
    { key: 'C', value: c },
  ];
  const ranked0 = [...raw].sort((a, b) => b.value - a.value);
  const topValue = ranked0[0].value;
  const mean = total / 4;
  // Threshold: significativa se ≥ 75% do topo e ≥ média (o topo é sempre significativo).
  const nearTop = topValue * 0.75;

  const ranked: DiscDimShare[] = ranked0.map((dim, idx) => {
    const significant = idx === 0 || (dim.value >= nearTop && dim.value >= mean && dim.value > 0);
    return {
      key: dim.key,
      label: DISC_META[dim.key].label,
      fill: DISC_META[dim.key].fill,
      value: dim.value,
      share: total > 0 ? (dim.value / total) * 100 : 0,
      significant,
    };
  });

  const sig = ranked.filter((r) => r.significant);
  const pattern = sig.map((r) => r.key).join('');
  const patternLabel = sig.map((r) => r.label).join(' + ');

  return { x, y, pattern, patternLabel, ranked };
}

// ---- Narrativa detalhada do DISC ----
interface DiscTraitCopy {
  /** Frase que descreve a dimensão quando é forte (compõe o "porquê"). */
  strong: string;
  /** Uma ou duas palavras de comportamento típico. */
  keywords: string;
}

const DISC_TRAIT_COPY: Record<DiscKey, DiscTraitCopy> = {
  D: {
    strong:
      'a Dominância elevada indica uma pessoa direta, orientada a resultados e desafios, que gosta de assumir o controle, decidir rápido e ir direto ao ponto',
    keywords: 'foco em resultado, decisão rápida, postura assertiva',
  },
  I: {
    strong:
      'a Influência elevada aponta uma pessoa comunicativa, entusiasmada e persuasiva, que se conecta com facilidade, motiva o time e trabalha bem com pessoas',
    keywords: 'comunicação, entusiasmo, persuasão',
  },
  S: {
    strong:
      'a Estabilidade elevada revela uma pessoa constante, paciente e colaborativa, que valoriza a harmonia, mantém o ritmo e dá suporte de forma confiável',
    keywords: 'constância, paciência, cooperação',
  },
  C: {
    strong:
      'a Conformidade elevada mostra uma pessoa analítica, cuidadosa e orientada a qualidade, que preza por precisão, regras claras e trabalho bem-feito',
    keywords: 'análise, precisão, qualidade',
  },
};

const LOW_TRAIT_COPY: Record<DiscKey, string> = {
  D: 'evita imposição e confronto, preferindo ceder ou buscar consenso',
  I: 'é mais reservada na comunicação, com um estilo mais contido que expansivo',
  S: 'lida bem com mudança e urgência, sem precisar de tanta previsibilidade',
  C: 'é mais flexível com regras e detalhes, priorizando velocidade sobre precisão',
};

/** Descreve a leitura dos dois eixos (ritmo × foco) em texto. */
function describeAxes(x: number, y: number): string {
  const pace =
    y > 0.12
      ? 'ritmo mais acelerado e proativo'
      : y < -0.12
        ? 'ritmo mais reservado e ponderado'
        : 'ritmo equilibrado entre proatividade e ponderação';
  const focus =
    x > 0.12
      ? 'foco mais voltado a pessoas e relacionamentos'
      : x < -0.12
        ? 'foco mais voltado a tarefas e resultados'
        : 'foco equilibrado entre tarefas e pessoas';
  return `${pace}, com ${focus}`;
}

/** Monta um parágrafo detalhado explicando o padrão e o porquê. */
function buildDiscNarrative(point: DiscPoint): string {
  const sig = point.ranked.filter((r) => r.significant);
  const low = point.ranked.filter((r) => !r.significant);
  const pct = (r: DiscDimShare): string => `${Math.round(r.share)}%`;

  // 1) Padrão + composição
  const compo = sig
    .map((r, idx) => `${idx === 0 ? 'principalmente ' : ''}${r.label} (${r.key}, ${pct(r)})`)
    .join(sig.length > 1 ? ' e ' : '');
  const intro =
    sig.length === 1
      ? `o padrão é ${point.pattern} — concentra-se em ${compo}`
      : `o padrão é ${point.pattern} — uma combinação de ${compo}`;

  // 2) Porquê (descrição de cada dimensão significativa)
  const why = sig.map((r) => DISC_TRAIT_COPY[r.key].strong).join('; e ');

  // 3) Eixos
  const axes = describeAxes(point.x, point.y);

  // 4) Dimensões baixas (o que tende a NÃO aparecer)
  const lowText =
    low.length > 0
      ? ` Já ${low.map((r) => `${r.label} (${r.key}, ${pct(r)})`).join(' e ')} aparece(m) em menor grau: a pessoa ${low
          .map((r) => LOW_TRAIT_COPY[r.key])
          .join('; ')}.`
      : '';

  return `${intro}. Isso porque ${why}. No espectro, isso posiciona um ${axes}.${lowText}`;
}

/** Compara adaptado (agora) × natural (original) e explica a diferença. */
function buildDiscComparison(natural: DiscPoint, adapted: DiscPoint | null): string | null {
  if (!adapted) return null;
  if (adapted.pattern === natural.pattern) {
    return `O perfil adaptado (como você age hoje, no trabalho) mantém o mesmo padrão ${natural.pattern} do perfil natural (sua essência) — ou seja, o ambiente atual está bem alinhado ao seu estilo instintivo, exigindo pouca "encenação".`;
  }
  // Aponta quais dimensões subiram/desceram do natural para o adaptado.
  const naturalMap = new Map(natural.ranked.map((r) => [r.key, r.share]));
  const movers = adapted.ranked
    .map((r) => ({ key: r.key, label: r.label, delta: r.share - (naturalMap.get(r.key) ?? 0) }))
    .filter((m) => Math.abs(m.delta) >= 4)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const up = movers.filter((m) => m.delta > 0);
  const down = movers.filter((m) => m.delta < 0);
  const upText = up.length
    ? `está exigindo mais ${up.map((m) => `${m.label} (${m.key})`).join(' e ')}`
    : '';
  const downText = down.length
    ? `e menos ${down.map((m) => `${m.label} (${m.key})`).join(' e ')}`
    : '';
  const movement = [upText, downText].filter(Boolean).join(' ');
  return `Comparando os dois: sua essência (natural) é ${natural.pattern}, mas hoje você se apresenta como ${adapted.pattern}. Isso sugere que o contexto atual ${movement || 'está modulando levemente o seu estilo'} — quanto maior essa diferença, mais energia a adaptação costuma consumir no dia a dia.`;
}

const NARRATIVE_KICKER_SX = {
  display: 'block',
  fontSize: '0.68rem',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'text.secondary',
} as const;

const DISC_QUADRANTS = [
  { key: 'D', label: 'Dominância', x1: -1, x2: 0, y1: 0, y2: 1, fill: '#ef4444' },
  { key: 'I', label: 'Influência', x1: 0, x2: 1, y1: 0, y2: 1, fill: '#eab308' },
  { key: 'C', label: 'Conformidade', x1: -1, x2: 0, y1: -1, y2: 0, fill: '#3b82f6' },
  { key: 'S', label: 'Estabilidade', x1: 0, x2: 1, y1: -1, y2: 0, fill: '#22c55e' },
] as const;

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, children }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, height: '100%', border: '1px solid', borderColor: 'divider' }}>
    <Typography variant="overline" color="primary" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {subtitle}
      </Typography>
    )}
    <Box sx={{ mt: subtitle ? 0 : 1 }}>{children}</Box>
  </Paper>
);

const BehavioralChartsPreview: React.FC<BehavioralChartsPreviewProps> = ({ user, showHeader = true }) => {
  const discChartData = useMemo(
    () =>
      DISC_SERIES.map((d) => ({
        name: d.short,
        fullLabel: d.label,
        natural: getNumeric(user, d.key),
        adaptado: getNumeric(user, d.adaptedKey),
        natFill: d.fill,
      })),
    [user],
  );

  const valoresChartData = useMemo(
    () =>
      VALORES_COMPORTAMENTAIS.map((v) => ({
        name: v.label,
        value: getNumeric(user, v.key),
        fill: v.fill,
      })).sort((a, b) => b.value - a.value),
    [user],
  );

  const radarData = useMemo(
    () =>
      TRAIT_ROWS.map((row) => ({
        trait: row.label,
        natural: getNumeric(user, row.natural),
        adaptado: getNumeric(user, row.adapted),
      })),
    [user],
  );

  const spectrumNatural = useMemo(
    () =>
      discSpectrumPoint(
        getNumeric(user, 'discD'),
        getNumeric(user, 'discI'),
        getNumeric(user, 'discS'),
        getNumeric(user, 'discC'),
      ),
    [user],
  );
  const spectrumAdapted = useMemo(
    () =>
      discSpectrumPoint(
        getNumeric(user, 'discDAdapted'),
        getNumeric(user, 'discIAdapted'),
        getNumeric(user, 'discSAdapted'),
        getNumeric(user, 'discCAdapted'),
      ),
    [user],
  );

  const naturalNarrative = useMemo(
    () => (spectrumNatural ? buildDiscNarrative(spectrumNatural) : null),
    [spectrumNatural],
  );
  const adaptedNarrative = useMemo(
    () => (spectrumAdapted ? buildDiscNarrative(spectrumAdapted) : null),
    [spectrumAdapted],
  );
  const spectrumComparison = useMemo(
    () => (spectrumNatural ? buildDiscComparison(spectrumNatural, spectrumAdapted) : null),
    [spectrumNatural, spectrumAdapted],
  );

  const barMax = (rows: { value: number }[]): number => {
    const m = Math.max(0, ...rows.map((r) => r.value));
    return Math.max(5, m);
  };

  const discYAxisMax = useMemo(() => {
    const vals = DISC_SERIES.flatMap((d) => [getNumeric(user, d.key), getNumeric(user, d.adaptedKey)]);
    const m = Math.max(0, ...vals);
    return Math.max(100, m);
  }, [user]);

  const valoresYAxisMax = useMemo(
    () => Math.max(60, barMax(valoresChartData)),
    [valoresChartData],
  );

  const radarMax = useMemo(() => {
    const vals = TRAIT_ROWS.flatMap((r) => [
      getNumeric(user, r.natural),
      getNumeric(user, r.adapted),
    ]);
    const m = Math.max(0, ...vals);
    return Math.max(5, m);
  }, [user]);

  const hasAnyData = useMemo(() => {
    if (!user) return false;
    const keys: BehavioralFormKey[] = [
      ...DISC_SERIES.flatMap((d) => [d.key, d.adaptedKey]),
      ...VALORES_COMPORTAMENTAIS.map((v) => v.key),
      ...TRAIT_ROWS.flatMap((r) => [r.natural, r.adapted]),
    ];
    return keys.some((k) => getNumeric(user, k) > 0);
  }, [user]);

  if (!hasAnyData) {
    return (
      <Typography color="text.secondary">
        Ainda não há dados de perfil comportamental cadastrados para você. Quando o RH registrar as análises,
        os gráficos aparecerão aqui.
      </Typography>
    );
  }

  return (
    <Box component="section">
      {showHeader && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
            Perfil comportamental
          </Typography>
          <Typography color="text.secondary">
            Visualização dos mesmos gráficos da área de perfil comportamental (somente leitura).
          </Typography>
        </Box>
      )}

      {/* Leitura detalhada do espectro DISC (agora × original) — no topo da seção */}
      {spectrumNatural && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, sm: 2.5 }, mb: 2, border: '1px solid', borderColor: 'divider' }}
        >
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
            Leitura do espectro DISC
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {spectrumAdapted && adaptedNarrative ? (
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderLeft: '3px solid', borderColor: RED }}>
                <Typography component="span" sx={NARRATIVE_KICKER_SX}>
                  Agora (perfil adaptado)
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Neste momento, seu espectro é{' '}
                  <Box component="strong" sx={{ fontWeight: 800 }}>{spectrumAdapted.pattern}</Box>
                  {': '}
                  {adaptedNarrative}
                </Typography>
              </Box>
            ) : null}

            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderLeft: '3px solid', borderColor: BLUE }}>
              <Typography component="span" sx={NARRATIVE_KICKER_SX}>
                Original (perfil natural)
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Na sua essência, seu espectro é{' '}
                <Box component="strong" sx={{ fontWeight: 800 }}>{spectrumNatural.pattern}</Box>
                {': '}
                {naturalNarrative}
              </Typography>
            </Box>

            {spectrumComparison ? (
              <Typography variant="body2" color="text.secondary">
                {spectrumComparison}
              </Typography>
            ) : null}
          </Stack>
        </Paper>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title="DISC" subtitle="Perfil natural e adaptado por dimensão.">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={discChartData}
                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                barGap={4}
                barCategoryGap="18%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                <XAxis dataKey="name" tick={{ fill: AXIS_TICK, fontSize: 12 }} tickLine={false} axisLine={{ stroke: GRID_STROKE }} />
                <YAxis domain={[0, discYAxisMax]} tick={{ fill: AXIS_TICK, fontSize: 11 }} tickLine={false} axisLine={{ stroke: GRID_STROKE }} />
                <ReferenceLine y={50} {...refLineStyle} />
                <ReferenceLine y={100} {...refLineStyle} />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  formatter={(value: number, name: string) => [
                    value,
                    name === 'natural' ? 'Perfil' : 'Adaptado',
                  ]}
                  labelFormatter={(_, p) => (p?.[0]?.payload as { fullLabel?: string })?.fullLabel ?? ''}
                  labelStyle={{ color: '#000000', fontWeight: 700 }}
                  contentStyle={TOOLTIP_STYLE}
                />
                <Legend
                  wrapperStyle={{ paddingTop: 8 }}
                  formatter={(value) => (
                    <span style={{ color: AXIS_TICK }}>
                      {value === 'natural' ? 'Perfil' : value === 'adaptado' ? 'Adaptado' : value}
                    </span>
                  )}
                />
                <Bar dataKey="natural" name="natural" fill={BLUE}>
                  {discChartData.map((entry) => (
                    <Cell key={`disc-n-${entry.name}`} fill={entry.natFill} />
                  ))}
                </Bar>
                <Bar dataKey="adaptado" name="adaptado" fill={DISC_ADAPTED_BAR_FILL} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="DISC — espectro"
            subtitle="Mapa de tendência: ritmo (acelerado ↔ reservado) × foco (tarefa ↔ pessoas)."
          >
            {spectrumNatural ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart margin={{ top: 12, right: 12, bottom: 12, left: 12 }}>
                    {/* Quadrantes coloridos por dimensão DISC */}
                    {DISC_QUADRANTS.map((q) => (
                      <ReferenceArea
                        key={q.key}
                        x1={q.x1}
                        x2={q.x2}
                        y1={q.y1}
                        y2={q.y2}
                        fill={q.fill}
                        fillOpacity={0.08}
                        stroke={GRID_STROKE}
                        strokeOpacity={0.6}
                        label={{ value: q.key, position: 'center', fill: q.fill, fontSize: 22, fontWeight: 800, opacity: 0.55 }}
                      />
                    ))}
                    <ReferenceLine x={0} stroke={REF_LINE} strokeWidth={1} />
                    <ReferenceLine y={0} stroke={REF_LINE} strokeWidth={1} />
                    <XAxis
                      type="number"
                      dataKey="x"
                      domain={[-1, 1]}
                      ticks={[-1, 0, 1]}
                      tickFormatter={(v: number) => (v < 0 ? 'Tarefa' : v > 0 ? 'Pessoas' : '')}
                      tick={{ fill: AXIS_TICK, fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: GRID_STROKE }}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      domain={[-1, 1]}
                      ticks={[-1, 0, 1]}
                      tickFormatter={(v: number) => (v < 0 ? 'Reservado' : v > 0 ? 'Acelerado' : '')}
                      tick={{ fill: AXIS_TICK, fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: GRID_STROKE }}
                      width={64}
                    />
                    <ZAxis range={[220, 220]} />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      contentStyle={TOOLTIP_STYLE}
                      labelStyle={{ color: '#000000', fontWeight: 700 }}
                      formatter={(_v, _n, p) => {
                        const d = p?.payload as { label?: string; pattern?: string; patternLabel?: string };
                        return [`${d?.pattern ?? ''} · ${d?.patternLabel ?? ''}`, d?.label ?? ''];
                      }}
                    />
                    <Scatter
                      name="Perfil"
                      data={[{ ...spectrumNatural, label: 'Perfil' }]}
                      fill={BLUE}
                      shape="circle"
                    />
                    {spectrumAdapted ? (
                      <Scatter
                        name="Adaptado"
                        data={[{ ...spectrumAdapted, label: 'Adaptado' }]}
                        fill={RED}
                        shape="diamond"
                      />
                    ) : null}
                    <Legend formatter={(value) => <span style={{ color: AXIS_TICK }}>{value}</span>} />
                  </ScatterChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 1.5 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                    <Typography variant="body2" color="text.secondary">
                      Padrão (blend):
                    </Typography>
                    {/* Letras: significativas em destaque, demais esmaecidas */}
                    <Stack direction="row" spacing={0.5}>
                      {spectrumNatural.ranked.map((r) => (
                        <Box
                          key={r.key}
                          title={`${r.label} · ${Math.round(r.share)}%`}
                          sx={{
                            width: 26,
                            height: 26,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 800,
                            fontSize: '0.85rem',
                            color: r.significant ? '#fff' : 'text.secondary',
                            bgcolor: r.significant ? r.fill : 'action.hover',
                            opacity: r.significant ? 1 : 0.7,
                          }}
                        >
                          {r.key}
                        </Box>
                      ))}
                    </Stack>
                    <Typography component="strong" sx={{ fontWeight: 800 }}>
                      {spectrumNatural.pattern}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {spectrumNatural.patternLabel}
                  </Typography>

                  {/* Barra de composição (share de cada dimensão) */}
                  <Box
                    sx={{
                      mt: 1,
                      display: 'flex',
                      width: '100%',
                      height: 10,
                      overflow: 'hidden',
                      bgcolor: 'action.hover',
                    }}
                    aria-label="Composição DISC"
                  >
                    {spectrumNatural.ranked.map((r) => (
                      <Box
                        key={r.key}
                        title={`${r.key} · ${r.label} · ${Math.round(r.share)}%`}
                        sx={{
                          width: `${r.share}%`,
                          bgcolor: r.fill,
                          opacity: r.significant ? 1 : 0.45,
                        }}
                      />
                    ))}
                  </Box>
                  <Stack direction="row" spacing={1.5} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                    {spectrumNatural.ranked.map((r) => (
                      <Typography
                        key={r.key}
                        variant="caption"
                        sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
                      >
                        <Box component="span" sx={{ fontWeight: 800, color: r.fill }}>{r.key}</Box>{' '}
                        {Math.round(r.share)}%
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              </>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Sem dados DISC suficientes para o espectro.
              </Typography>
            )}
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ChartCard title="Valores comportamentais" subtitle="Ordenados do maior para o menor.">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={valoresChartData} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                <XAxis
                  dataKey="name"
                  interval={0}
                  angle={-28}
                  textAnchor="end"
                  height={56}
                  tick={{ fill: AXIS_TICK, fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: GRID_STROKE }}
                />
                <YAxis domain={[0, valoresYAxisMax]} tick={{ fill: AXIS_TICK, fontSize: 11 }} tickLine={false} axisLine={{ stroke: GRID_STROKE }} />
                <ReferenceLine y={30} {...refLineStyle} />
                <ReferenceLine y={60} {...refLineStyle} />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  formatter={(value: number) => [value, 'Valor']}
                  labelStyle={{ color: '#000000', fontWeight: 700 }}
                  contentStyle={TOOLTIP_STYLE}
                />
                <Bar dataKey="value" fill={BLUE}>
                  {valoresChartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ChartCard
            title="Natural × Adaptado"
            subtitle="Gráfico em malha polar: dimensões natural (azul) e adaptado (vermelho)."
          >
            <ResponsiveContainer width="100%" height={420}>
              <RadarChart cx="50%" cy="52%" outerRadius="72%" data={radarData}>
                <PolarGrid stroke={GRID_STROKE} />
                <PolarAngleAxis dataKey="trait" tick={{ fill: AXIS_TICK, fontSize: 10 }} />
                <PolarRadiusAxis
                  angle={30}
                  domain={[0, radarMax]}
                  tick={{ fill: AXIS_TICK, fontSize: 10 }}
                />
                <Radar
                  name="Natural"
                  dataKey="natural"
                  stroke={BLUE}
                  fill={BLUE}
                  fillOpacity={0.25}
                  strokeWidth={2}
                  dot={{ r: 3, fill: BLUE }}
                />
                <Radar
                  name="Adaptado"
                  dataKey="adaptado"
                  stroke={RED}
                  fill={RED}
                  fillOpacity={0.2}
                  strokeWidth={2}
                  dot={{ r: 3, fill: RED }}
                />
                <Legend formatter={(value) => <span style={{ color: AXIS_TICK }}>{value}</span>} />
                <Tooltip
                  cursor={false}
                  labelStyle={{ color: '#000000', fontWeight: 700 }}
                  contentStyle={TOOLTIP_STYLE}
                />
              </RadarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BehavioralChartsPreview;
